import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchStockMovements } from '../../../../../../../../jsx/actions/stockMovementAction';
import { TableLayout } from '../../containers/Layout';
import { 
    TableRow, 
    TableCell, 
    Typography, 
    TableHead, 
    TableBody, 
    Box, 
    Chip,
    Tooltip,
    Button
} from '@material-ui/core';
import { 
    amber, 
    blue, 
    blueGrey, 
    cyan, 
    green, 
    indigo, 
    purple, 
    red, 
    orange, 
    lightGreen,
    grey
} from '@material-ui/core/colors';
import { toast } from 'react-toastify';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import TrendingDownIcon from '@material-ui/icons/TrendingDown';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import Currency from '../../../../../commonModules/Currency';
import EcomSourceBadge from '../../containers/EcomSourceBadge';
import { isEcomSale } from '../../../../../../../../jsx/utils/ecomSale';

const SingleHistoryEnhanced = (props) => {
    const dispatch = useDispatch();
    const info = props.stockID.split("|");
    const selectedStockStatus = (info[2] || "").toUpperCase();
    const statusOnlyTabs = new Set([
        "WAITING_DELIVERY",
        "COMMERCIAL",
        "RESERVATION",
        "ARRIVAGE",
        "DEMAND",
        "DEMANDE",
        "INSTALLATION",
        "LEASING",
        "OUT",
        "OCCASION"
    ]);
    const statusOnlyMode = statusOnlyTabs.has(selectedStockStatus);
    const normalizeStatus = (status = "") => {
        const normalized = String(status).trim().toUpperCase();
        if (normalized === "DEMAND") return "DEMANDE";
        return normalized;
    };
    const selectedStatusNormalized = normalizeStatus(selectedStockStatus);
    const getStatusSuffix = (status) => {
        switch (normalizeStatus(status)) {
            case "WAITING_DELIVERY": return "en transit";
            case "COMMERCIAL": return "commercial";
            case "RESERVATION": return "reservation";
            case "ARRIVAGE": return "arrivage";
            case "DEMANDE": return "demande";
            case "INSTALLATION": return "installation";
            case "LEASING": return "leasing";
            case "OUT": return "cash";
            case "OCCASION": return "occasion";
            default: return "";
        }
    };
    const movementMatchesSelectedStatus = (movement) => {
        if (!statusOnlyMode) return true;
        const movementStatus = normalizeStatus(movement.status);
        if (selectedStatusNormalized === "WAITING_DELIVERY") {
            return movementStatus === "WAITING_DELIVERY" || !!movement.is_transit;
        }
        return movementStatus === selectedStatusNormalized;
    };
    
    // Sélecteurs Redux
    const CLIENTS = useSelector((state) => state.inventory.clients); 
    const AGENTS = useSelector((state) => state.inventory.agents); 
    const SHOPS = useSelector((state) => state.inventory.shops);
    
    // Utiliser le state Redux pour les mouvements
    const movements = useSelector((state) => state.stockMovements?.movements || []);
    const currentStock = useSelector((state) => state.stockMovements?.currentStock || 0);
    const loading = useSelector((state) => state.stockMovements?.loading || false);
    const error = useSelector((state) => state.stockMovements?.error);
    const [shop, setShop] = useState("");
    const [product, setProduct] = useState("");
    const [showDetailColumn, setShowDetailColumn] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const shopId = info[0];
                const productId = info[1];
                
                setShop(shopId);
                setProduct(productId);

                // Dispatch l'action Redux (le state sera mis à jour automatiquement)
                const movementsList = await dispatch(fetchStockMovements(shopId, productId));
                
                if (!movementsList || movementsList.length === 0) {
                    toast.warning("Aucun mouvement trouvé pour ce stock");
                }
                
                // Afficher un avertissement si des mouvements ont été ignorés
                // (sera mis à jour via le state Redux)
            } catch (error) {
                console.error('Error fetching movements:', error);
                toast.error("Erreur lors de la récupération de l'historique");
            }
        };
        fetchData();
    }, [dispatch, props.stockID]);

    // Fonction pour obtenir la couleur selon le type de mouvement
    const getMovementStyle = (movement) => {
        const style = {
            fontWeight: 400,
        };

        switch (movement.movement_type) {
            case "IN":
                style.background = lightGreen[100];
                style.color = green[900];
                break;
            case "OUT":
                style.background = orange[100];
                style.color = orange[900];
                break;
            case "INTERNAL":
                style.background = blueGrey[100];
                style.color = blueGrey[900];
                break;
            default:
                style.background = grey[100];
        }

        // Style spécial pour les éléments supprimés
        if (movement.is_deleted) {
            style.textDecoration = "line-through";
            style.color = "dimgrey";
            style.fontStyle = "italic";
        }

        // Style spécial pour les avoirs
        if (movement.transfer_id && movement.transfer_id.startsWith("AV_")) {
            style.fontStyle = "italic";
            style.background = purple[50];
        }

        // Mouvement informatif en transit: affiché, mais sans impact stock
        if (movement.is_transit) {
            style.background = cyan[50];
            style.color = blueGrey[900];
        }

        return style;
    };

    // Fonction pour obtenir l'icône selon le type
    const getMovementIcon = (movementType) => {
        switch (movementType) {
            case "IN":
                return <TrendingUpIcon style={{ color: green[600], verticalAlign: 'middle' }} />;
            case "OUT":
                return <TrendingDownIcon style={{ color: orange[600], verticalAlign: 'middle' }} />;
            case "INTERNAL":
                return <SwapHorizIcon style={{ color: blueGrey[600], verticalAlign: 'middle' }} />;
            default:
                return null;
        }
    };

    // Fonction pour formater la destination/source
    const getLocationDisplay = (movement) => {
        if (movement.movement_type === "IN") {
            // Afficher la flèche pour les réappros entrants (shop_source → in_shop)
            if (movement.shop_source && movement.shop_source !== "") {
                return (
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2">
                            {SHOPS && SHOPS[movement.shop_source] ? SHOPS[movement.shop_source].name : movement.shop_source}
                        </Typography>
                        <ArrowForwardIcon fontSize="small" style={{ color: grey[500] }} />
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                            {SHOPS && SHOPS[movement.in_shop] ? SHOPS[movement.in_shop].name : movement.in_shop}
                        </Typography>
                    </Box>
                );
            }
            // Sinon afficher seulement la destination
            return (
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">
                        {SHOPS && SHOPS[movement.in_shop] ? SHOPS[movement.in_shop].name : movement.in_shop}
                    </Typography>
                </Box>
            );
        } else if (movement.movement_type === "OUT") {
            // Si shop_dest est défini, afficher la flèche avec destination
            if (movement.shop_dest && movement.shop_dest !== "") {
                return (
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2">
                            {SHOPS && SHOPS[movement.shop_source] ? SHOPS[movement.shop_source].name : movement.shop_source}
                        </Typography>
                        <ArrowForwardIcon fontSize="small" style={{ color: grey[500] }} />
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                            {SHOPS && SHOPS[movement.shop_dest] ? SHOPS[movement.shop_dest].name : movement.shop_dest}
                        </Typography>
                    </Box>
                );
            } else if (movement.out_shop && movement.out_shop !== "") {
                // Fallback: utiliser out_shop du RFIDHistory
                return (
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2">
                            {SHOPS && SHOPS[movement.shop_source || movement.in_shop] ? SHOPS[movement.shop_source || movement.in_shop].name : (movement.shop_source || movement.in_shop)}
                        </Typography>
                        <ArrowForwardIcon fontSize="small" style={{ color: grey[500] }} />
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                            {SHOPS && SHOPS[movement.out_shop] ? SHOPS[movement.out_shop].name : movement.out_shop}
                        </Typography>
                    </Box>
                );
            } else {
                // Pas de destination définie (vente, installation)
                return (
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2">
                            {SHOPS && SHOPS[movement.shop_source] ? SHOPS[movement.shop_source].name : movement.shop_source}
                        </Typography>
                    </Box>
                );
            }
        }
        return "-";
    };

    // Fonction pour obtenir le client/commercial
    const getClientDisplay = (movement) => {
        if (movement.client_id) {
            return CLIENTS && CLIENTS[movement.client_id] 
                ? CLIENTS[movement.client_id].client_lastname 
                : "";
        } else if (movement.commercial_id) {
            return AGENTS && AGENTS[movement.commercial_id] 
                ? AGENTS[movement.commercial_id].name 
                : "";
        }
        return "-";
    };

    // Calcul du stock progressif
    const renderMovements = () => {
        const colSpan = showDetailColumn ? 8 : 7;
        if (loading) {
            return (
                <TableRow>
                    <TableCell colSpan={colSpan} align="center">
                        <Typography>Chargement...</Typography>
                    </TableCell>
                </TableRow>
            );
        }

        if (movements.length === 0) {
            return (
                <TableRow>
                    <TableCell colSpan={colSpan} align="center">
                        <Typography color="textSecondary">Aucun mouvement trouvé</Typography>
                    </TableCell>
                </TableRow>
            );
        }

        // ✅ FILTRE: Éliminer les doublons/fragments créés automatiquement lors des divisions de transferts
        // (même logique que SingleHistory.jsx lignes 52-54)
        const filteredMovements = movements.filter(movement => {
            // Skip items created automatically during transfer splits
            // These are fragments and should not be displayed separately
            // if (movement.created_by && movement.created_from_history) {
            //     return false;
            // }
            if (!movementMatchesSelectedStatus(movement)) {
                return false;
            }
            return true;
        });

        // ✅ TRI: Trier par date décroissante (plus récent en premier) pour garantir l'ordre
        const sortedMovements = [...filteredMovements].sort((a, b) => {
            const dateA = new Date(a.creation_date || a.date || 0);
            const dateB = new Date(b.creation_date || b.date || 0);
            return dateB - dateA; // Décroissant (plus récent en premier)
        });

        // ✅ FIX: Calculer le stock progressif du plus ancien au plus récent
        // puis afficher dans l'ordre inverse (plus récent en premier)
        
        // 1. Calculer les stocks pour chaque mouvement (du plus ancien au plus récent)
        const movementsReversed = [...sortedMovements].reverse();
        let runningStock = 0;
        const movementsWithStock = movementsReversed.map((movement) => {
            if (movement.is_deleted == false) {
                runningStock += movement.impact_qty || 0;
            }
            return {
                ...movement,
                stockAfterMovement: runningStock
            };
        });
        
        // 2. Afficher dans l'ordre original (plus récent en premier)
        const movementsToDisplay = movementsWithStock.reverse();

        return movementsToDisplay.map((movement, index) => {
            const style = getMovementStyle(movement);
            const date = new Date(movement.creation_date || movement.date);

            return (
                <TableRow key={movement.id || index} style={style}>
                    {/* Date */}
                    <TableCell style={style}>
                        {date.toLocaleString('fr-FR')}
                    </TableCell>
                    
                    {/* Type avec icône */}
                    <TableCell style={style}>
                        <Box display="flex" alignItems="center" gap={1}>
                            {getMovementIcon(movement.movement_type)}
                            <Typography variant="body2">{movement.movement_label}</Typography>
                        </Box>
                        {/* Vente venue d'une boutique en ligne : origine et référence de commande. */}
                        {isEcomSale(movement) &&
                            <Tooltip title={movement.transfer_ref ? `Vente ${movement.transfer_ref}` : ''}>
                                <Box display="flex" alignItems="center" mt={0.5} style={{ gap: 6 }}>
                                    <EcomSourceBadge source={movement.source} url={movement.ecom_order_url} />
                                    {movement.external_ref &&
                                        <Typography variant="caption">{movement.external_ref}</Typography>
                                    }
                                </Box>
                            </Tooltip>
                        }
                    </TableCell>
                    
                    {/* Quantité avec couleur */}
                    <TableCell style={style}>
                        {(() => {
                            const transitQty = Math.abs(movement.quantity || 0);
                            const movementStatusSuffix = getStatusSuffix(movement.status);
                            const statusQtyMode = statusOnlyMode && movementStatusSuffix !== "";
                            const impactQty = movement.impact_qty ?? 0;
                            const hasUncountedStock = transitQty > 0 && (impactQty === 0 || movement.is_transit);
                            const uncountedSuffix = hasUncountedStock
                                ? ` (${transitQty} ${movementStatusSuffix || "non comptabilisé"})`
                                : "";
                            const qtyLabel = statusQtyMode
                                ? `+${transitQty} (${movementStatusSuffix})`
                                : (impactQty > 0 ? `+${impactQty}` : impactQty) + uncountedSuffix;
                            const chipColor = movement.is_transit
                                ? blueGrey[500]
                                : (statusQtyMode ? blue[500] : (impactQty > 0 ? green[500] : orange[500]));
                            return (
                        <Chip
                            size="small"
                            label={qtyLabel}
                            style={{
                                backgroundColor: chipColor,
                                color: 'white',
                                fontWeight: 600
                            }}
                        />
                            );
                        })()}
                    </TableCell>
                    
                    {/* Stock après mouvement */}
                    <TableCell style={style}>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                            {movement.stockAfterMovement}
                        </Typography>
                    </TableCell>
                    
                    {/* Location (Shop source → dest) */}
                    <TableCell style={style}>
                        {getLocationDisplay(movement)}
                    </TableCell>
                    
                    {/* Client / Commercial */}
                    <TableCell style={style}>
                        <Typography variant="body2">
                            {getClientDisplay(movement)}
                        </Typography>
                    </TableCell>
                    
                    {/* Détail RFID (colonne optionnelle) */}
                    {showDetailColumn && (
                        <TableCell style={style}>
                            <Typography variant="caption" color="textSecondary">
                                {movement.detail || "—"}
                            </Typography>
                        </TableCell>
                    )}
                    
                    {/* Prix */}
                    <TableCell style={style} align="right">
                        {movement.cost ? (
                            <Tooltip title={`PU: ${movement.priceU?.toFixed(2) || 0} €`}>
                                <Typography variant="body2">
                                    <Currency value={movement.cost} />
                                </Typography>
                            </Tooltip>
                        ) : "-"}
                    </TableCell>
                </TableRow>
            );
        });
    };

    // Filtrer les doublons avant l'affichage (même logique que dans renderMovements)
    const filteredMovementsForCount = movements.filter(movement => {
        if (movement.created_by && movement.created_from_history) {
            return false;
        }
        if (!movementMatchesSelectedStatus(movement)) {
            return false;
        }
        return true;
    });


    return (
        <Box>
            {/* En-tête avec stock actuel */}
            <Box mb={2} p={2} style={{ backgroundColor: grey[100], borderRadius: 8 }}>
                <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                    <Chip
                        label={`Stock actuel: ${currentStock}`}
                        style={{
                            backgroundColor: currentStock > 0 ? green[500] : red[500],
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '16px'
                        }}
                    />
                    <Typography variant="body2" color="textSecondary" style={{ paddingLeft: '16px' }}>
                        {filteredMovementsForCount.length} mouvement(s)
                    </Typography>
                    <Button
                        size="small"
                        startIcon={showDetailColumn ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        onClick={() => setShowDetailColumn(prev => !prev)}
                        style={{ marginLeft: 8 }}
                    >
                        {showDetailColumn ? "Masquer détail RFID" : "Afficher détail RFID"}
                    </Button>
                </Box>
            </Box>

            {/* Tableau */}
            <TableLayout>
                <TableHead>
                    <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Nature</TableCell>
                        <TableCell>Quantité</TableCell>
                        <TableCell>Stock Après</TableCell>
                        <TableCell>Localisation</TableCell>
                        <TableCell>Client / Commercial</TableCell>
                        {showDetailColumn && <TableCell>Détail RFID</TableCell>}
                        <TableCell align="right">Coût</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {renderMovements()}
                </TableBody>
            </TableLayout>
        </Box>
    );
}

export default SingleHistoryEnhanced;

